import React from "react";
import { ReactComponent as DeleteFile } from "../assets/images/icons/delete-small.svg";

/**
 * Generic multi-select checkbox group used by every Patient Narrative
 * multi-select question (qualifying events, medical history sources,
 * supplementary sources) - one configurable component instead of three
 * bespoke ones, per the review's "do not turn this into a form framework,
 * but do not hand-roll the same checkbox list three times either".
 *
 * - `exclusiveValue`: a value that clears every other selection when
 *   chosen, and is itself cleared when any other value is chosen
 *   (`cioms_only`, `none`). Omit for a plain multi-select with no
 *   mutually-exclusive option.
 * - `otherValue` + `otherTexts` + `onOtherTextsChange`: when set, selecting
 *   this value reveals a repeatable list of free-text fields (a qualifying
 *   event described as "other" can be more than one distinct thing, e.g.
 *   "Cardiac arrhythmia" and "Renal failure" both need their own row);
 *   deselecting it clears the whole list in the same
 *   `onChange`/`onOtherTextsChange` pair, so a stale value never lingers to
 *   be rejected by the backend's `other selected <-> other_texts populated`
 *   structural rule.
 */
const CheckboxGroupWithOther = ({
  name,
  value = [],
  onChange,
  options,
  exclusiveValue,
  otherValue,
  otherTexts = [],
  onOtherTextsChange,
  otherPlaceholder = "Please specify",
  disabled = false,
}) => {
  const toggle = optionValue => {
    const isSelected = value.includes(optionValue);
    let next;

    if (optionValue === exclusiveValue) {
      next = isSelected ? [] : [exclusiveValue];
    } else if (isSelected) {
      next = value.filter(v => v !== optionValue);
    } else {
      next = [...value.filter(v => v !== exclusiveValue), optionValue];
    }

    if (optionValue === otherValue && isSelected && onOtherTextsChange) {
      // "other" is being deselected - clear its text rows in the same
      // interaction, before the next save, so a stale value never lingers.
      onOtherTextsChange([]);
    }

    onChange(next);
  };

  // Always render at least one row while "other" is selected, so the field
  // never appears to vanish - mirrors the single-input UX this replaced.
  const otherRows = otherTexts.length > 0 ? otherTexts : [""];

  const updateOtherRow = (index, text) => {
    const next = [...otherRows];
    next[index] = text;
    onOtherTextsChange(next);
  };

  const addOtherRow = () => {
    onOtherTextsChange([...otherRows, ""]);
  };

  const removeOtherRow = index => {
    onOtherTextsChange(otherRows.filter((_, i) => i !== index));
  };

  return (
    <div className="questions-content__input">
      {options.map(choice => (
        <div key={choice.value} className="checkbox-group-with-other__row">
          <input
            type="checkbox"
            id={`${name}-${choice.value}`}
            checked={value.includes(choice.value)}
            disabled={disabled}
            onChange={() => toggle(choice.value)}
          />
          <label htmlFor={`${name}-${choice.value}`}>{choice.title}</label>
        </div>
      ))}
      {otherValue && value.includes(otherValue) && (
        <div className="checkbox-group-with-other__other-list">
          {otherRows.map((text, index) => (
            <div key={index} className="checkbox-group-with-other__other-row">
              <input
                type="text"
                className="input-text checkbox-group-with-other__other-text"
                value={text}
                disabled={disabled}
                placeholder={otherPlaceholder}
                onChange={e => updateOtherRow(index, e.target.value)}
              />
              {otherRows.length > 1 && (
                <button
                  type="button"
                  className="checkbox-group-with-other__remove-other-row"
                  disabled={disabled}
                  onClick={() => removeOtherRow(index)}
                  aria-label={`Remove ${otherPlaceholder.toLowerCase()} ${index + 1}`}>
                  <DeleteFile />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            className="checkbox-group-with-other__add-other-row"
            disabled={disabled}
            onClick={addOtherRow}>
            + Add another
          </button>
        </div>
      )}
    </div>
  );
};

export default CheckboxGroupWithOther;
